<!-- eslint-disable vue/prefer-true-attribute-shorthand -->
<template>
  <ais-instant-search
    :index-name="algolia.indexName"
    :routing="algolia.routing"
    :search-client="algolia.searchClient"
    :class-names="{
      'ais-InstantSearch': 'main-collection critical-component-clear',
    }"
    :insights="algolia.middlewares"
  >
    <!-- Loading State -->
    <algolia-skeleton v-if="!hydrated && !useSearch" />

    <ais-configure
      :distinct="true"
      :analytics="true"
      :faceting-after-distinct.camel="true"
      :filters="algolia.filters"
      :hits-per-page.camel="perPage"
      :rule-contexts.camel="ruleContexts"
      :max-values-per-facet.camel="1000"
      :max-facet-hits.camel="100"
    />

    <algolia-search
      v-if="useSearch"
      :is-active="isSearchPage || isActive"
      :banners="banners"
      :no-results-collection="noResultsCollection"
    />

    <template v-else>
      <div
        v-show="hydrated"
        class="top-bar"
      >
        <algolia-facets
          :nb-hits="nbHits"
        />
        <algolia-sort />
      </div>

      <algolia-product-list
        v-show="hydrated"
        :content-cards="contentCards"
        :content-cards-old="contentCardsOld"
        @nb-hits="updateHydrate"
      />
    </template>

    <div
      v-if="seo.title && seo.description"
      class="main-collection__seo"
    >
      <h2
        class="text-body-m-regular"
        v-text="seo.title"
      />
      <expanding-content
        class="main-collection__description text-body-s-regular"
        :text="seo.description"
        namespace="algolia"
      />
    </div>
  </ais-instant-search>
</template>

<script>

/**
 * Vue: Main Algolia (main-algolia)
 * -----------------------------------------------------------------------------
 * Default collection and search template powered by Algolia.
 *
 */
import algoliasearch from 'algoliasearch/lite'
import { history as historyRouter } from 'instantsearch.js/es/lib/routers'
import { singleIndex as singleIndexMapping } from 'instantsearch.js/es/lib/stateMappings'
import {
  AisConfigure,
  AisInstantSearch,
} from 'vue-instantsearch/vue3/es'

import AlgoliaProductList from '~async/main-algolia/algolia-product-list'
import AlgoliaFacets from '~async/main-algolia/algolia-facets'
import AlgoliaSort from '~async/main-algolia/algolia-sort'
import AlgoliaSkeleton from '~async/main-algolia/algolia-skeleton'
import AlgoliaSearch from '~async/algolia-search/algolia-search'

import { router, searchRouter } from '~/config/algolia'
import ExpandingContent from '~async/expanding-content/expanding-content'
import { getAlgoliaIndex } from '~/helpers/algolia'

// Algolia analytics
import aa from 'search-insights'
import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares'

const insightsMiddleware = createInsightsMiddleware({
  insightsClient: aa,
})

const algoliaClient = algoliasearch(
  window.cnvs.algolia.credentials.appId,
  window.cnvs.algolia.credentials.apiKey,
)

export default {
  name: 'MainAlgolia',

  components: {
    AlgoliaSkeleton,
    AisConfigure,
    AisInstantSearch,
    AlgoliaProductList,
    AlgoliaFacets,
    AlgoliaSearch,
    AlgoliaSort,
    ExpandingContent,
  },

  props: {
    banners: {
      type: Array,
      default: () => [],
    },
    contentCards: {
      type: Array,
      default: () => [],
    },
    contentCardsOld: {
      type: Object,
      default: () => ({}),
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    isSearch: {
      type: Boolean,
      default: false,
    },
    noResultsCollection: {
      type: Object,
      default: () => null,
    },
    seo: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      algolia: {
        filters: '',
        indexName: getAlgoliaIndex(),
        routing: {
          router: historyRouter(
            this.isSearchPage || this.isSearch
              ? searchRouter
              : router,
          ),
          stateMapping: singleIndexMapping(getAlgoliaIndex()),
        },
        searchClient: {
          ...algoliaClient,
          /**
           * Turn off analytics for empty searches
           * @see https://support.algolia.com/hc/en-us/articles/11013837385617-How-can-I-reduce-empty-searches-empty-queries-in-my-analytics
           * @see https://www.algolia.com/doc/guides/search-analytics/guides/exclude-queries/
           */
          search(requests) {
            const newRequests = requests.map((request) => {
              // Test for empty string and change `analytics` request parameter to `false`
              if (!request.params.query || request.params.query.length === 0) {
                request.params.analytics = false
              }
              return request
            })

            return algoliaClient.search(newRequests)
          },
        },
        middlewares: [insightsMiddleware],
      },
      hydrated: false,
      perPage: this.$variable('settings.pagination.products') || 24,
      nbHits: -1,
      isSearchPage: this.$variable('page.type') === 'search',
    }
  },

  computed: {

    /**
     * Compute if the component is being used for search purposes
     * @returns {Boolean}
     */
    useSearch() {
      return this.isSearch || this.$variable('page.type') === 'search'
    },

    /**
     * Compute rule contexts.
     * @returns {Array}
     */
    ruleContexts() {
      if (this.useSearch) {
        return ['search']
      }
      return [`${this.$variable('page.handle')}`]
    },
  },

  created() {
    this.setPageFilters()
    aa('setUserToken', sessionStorage.getItem('algolia_user_token'))
  },

  methods: {

    /**
     * Set the default filters for Algolia.
     * - No filter applied if we aren't on a collections page.
     * - Otherwise, applies a collection filter with the filter handle.
     */
    setPageFilters() {
      if (this.$variable('page.type') !== 'collection' || this.useSearch) {
        this.algolia.filters = ''
        return
      }

      this.algolia.filters = `collections:"${this.$variable('page.handle')}"`
    },

    /**
     * Set hydrate and nbHits
     * @param nbHits
     */
    updateHydrate(nbHits) {
      this.nbHits = nbHits
      if (this.hydrated) {
        return
      }
      this.hydrated = nbHits > -1
    },
  },
}
</script>
